import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaPaw, FaHeart, FaStar, FaUserMd } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Icon from './Icon';
import SectionTitle from './SectionTitle';

const theme = {
  primary: '#FF9D5C',
  primaryLight: '#FFB07D',
  secondary: '#2C3E50',
  accent: '#E67E22',
  background: '#F8F9FA',
  white: '#FFFFFF',
  text: '#2C3E50',
  textLight: '#6C7A89',
  border: '#E9ECEF'
};

const AboutSection = styled(motion.section)`
  position: relative;
  background: ${theme.background};
  padding: 40px 0;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 1rem;
    width: 100%;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.text};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, ${theme.primary}, ${theme.accent});
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  color: ${theme.textLight};
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2rem;
  align-items: start;
  position: relative;
  width: 100%;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  aspect-ratio: 4/3;
  height: auto;
  z-index: 1;
  max-width: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  @media (max-width: 968px) {
    aspect-ratio: 16/9;
    margin-bottom: 1rem;
    max-width: 100%;
    height: auto;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  z-index: 2;
  max-width: 450px;
  margin-left: auto;

  @media (max-width: 968px) {
    max-width: 100%;
    margin-left: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const MissionCard = styled.div`
  background: ${theme.white};
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid ${theme.border};
  transition: transform 0.3s ease;
  cursor: pointer;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const MissionTitle = styled.h3`
  color: ${theme.primary};
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
  gap: 0.5rem;

    @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;

const MissionText = styled.p`
    color: ${theme.text};
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const PawDecoration = styled.div`
  position: absolute;
  z-index: 1;
  opacity: 0.05;

  @media (max-width: 768px) {
    display: none;
  }

  &.paw1 {
    top: 10%;
    right: 5%;
    transform: rotate(45deg);
  }

  &.paw2 {
    bottom: 15%;
    left: 5%;
    transform: rotate(-30deg);
  }

  &.paw3 {
    top: 50%;
    right: 15%;
    transform: rotate(15deg);
  }

  &.paw4 {
    top: 25%;
    left: 20%;
    transform: rotate(-15deg);
  }
`;

interface AboutProps {
  id?: string;
}

const About: React.FC<AboutProps> = ({ id }) => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  return (
    <AboutSection id={id}>
      <Container>
        <SectionTitle>Quem Somos</SectionTitle>
        <SectionHeader>
              <Subtitle>
            Cuidado, experiência e ética no atendimento veterinário do seu pet
              </Subtitle>
        </SectionHeader>

        <ContentGrid>
          <ImageContainer data-aos="fade-right">
            <img src="/images/clinica.jpg" alt="Clínica Estima Pet" />
          </ImageContainer>

          <InfoContainer data-aos="fade-left">
            <MissionCard>
              <MissionTitle>
                <Icon icon={FaHeart} size={24} color={theme.primary} /> Nossa Missão
              </MissionTitle>
              <MissionText>
                Proporcionar cuidados excepcionais aos pets, combinando tecnologia avançada com um atendimento humanizado e carinhoso.
              </MissionText>
            </MissionCard>

            <MissionCard>
              <MissionTitle>
                <Icon icon={FaUserMd} size={24} color={theme.primary} /> Nossa Equipe
              </MissionTitle>
              <MissionText>
                Profissionais apaixonados e altamente qualificados, dedicados a oferecer o melhor atendimento veterinário.
              </MissionText>
            </MissionCard>

            <MissionCard>
              <MissionTitle>
                <Icon icon={FaStar} size={24} color={theme.primary} /> Nossa Estrutura
              </MissionTitle>
              <MissionText>
                Instalações modernas e equipadas com tecnologia de ponta para garantir diagnósticos precisos e tratamentos eficazes.
              </MissionText>
            </MissionCard>
          </InfoContainer>
        </ContentGrid>
      </Container>

      {/* Patinhas decorativas */}
      <PawDecoration className="paw1">
        <Icon icon={FaPaw} size={200} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw2">
        <Icon icon={FaPaw} size={250} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw3">
        <Icon icon={FaPaw} size={180} color="#000" />
      </PawDecoration>
      <PawDecoration className="paw4">
        <Icon icon={FaPaw} size={220} color="#000" />
      </PawDecoration>
    </AboutSection>
  );
};

export default About; 